<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">
    <!-- LOADING -->
    <Loading v-if="loading" :text="$t('views.health.loading')" />

    <!-- ERROR -->
    <div v-else-if="error" class="text-red-600 text-center">
      {{ error }}
    </div>

    <!-- SELECTION CARDS -->
    <div v-else class="space-y-4">
      <!-- Wounds Card -->
      <ModuleShell
        :title="$t('views.health.wounds')"
        :icon="AlertCircle"
        @click="goToWounds"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm opacity-60">{{ $t('views.health.woundsSubtitle') }}</p>
          <div class="text-2xl font-bold">{{ wounds.length }}</div>
        </div>
      </ModuleShell>

      <!-- Treatments Card -->
      <ModuleShell
        :title="$t('views.health.treatments')"
        :icon="Syringe"
        @click="goToTreatments"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm opacity-60">{{ $t('views.health.treatmentsSubtitle') }}</p>
          <div class="text-2xl font-bold">{{ treatments.length }}</div>
        </div>
      </ModuleShell>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHealthStore } from '@/stores/health.store.js'
import { storeToRefs } from 'pinia'

import Loading from '@/components/ui/feedback/Loading.vue'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { AlertCircle, Syringe } from 'lucide-vue-next'

const route = useRoute()
const router = useRouter()
const goatId = Number(route.params.id)

const healthStore = useHealthStore()
const { loading, error } = storeToRefs(healthStore)

const wounds = computed(() => healthStore.wounds(goatId))
const treatments = computed(() => healthStore.treatments(goatId))

function goToWounds() {
  router.push({ name: 'Wounds', params: { id: goatId } })
}

function goToTreatments() {
  router.push({ name: 'Treatments', params: { id: goatId } })
}

onMounted(async () => {
  await healthStore.fetchHealthRecordsByGoat(goatId)
})
</script>
